import { useCallback } from "react";
import { useReactFlow } from "@xyflow/react";
import { startConnectionTracking, stopConnectionTracking, resolveDropTarget } from "../../../flow-kit";
import { resolveStepIdFromNodeId } from "../../core/builder";
import { isGatewayNodeId, isEndNodeId, parseGatewayNodeId } from "../../core/constants";
import { buildRuleHandleId, extractRuleId } from "../../core/edgeClassifier";
function getPointerCoords(event) {
    if ("changedTouches" in event) {
        const touch = event.changedTouches[0];
        return touch ? {
            clientX: touch.clientX,
            clientY: touch.clientY
        } : null;
    }
    return {
        clientX: event.clientX,
        clientY: event.clientY
    };
}
export function useEditorConnections({ onConnectSteps }) {
    const { getNode } = useReactFlow();
    // Process-flow-specific drop resolution. Rejects synthetic ids that aren't
    // real connection destinations (start has no inputs; end has no outputs) and
    // redirects the synthetic `+` aggregator (`gateway-X`) onto its predecessor
    // step (`X`) when the drag started from a source (output) handle — the user
    // is dropping into the `+`'s input, which semantically means "fan into X's
    // inputs". When the drag started from a target (input) handle the cursor is
    // looking for an output to consume; the gateway id is preserved so the
    // hover preview snaps to the `+` itself — `handleConnectEnd` does the
    // gateway-to-step rewrite and rule-id recovery at drop time.
    //
    // Body-drops on a decision from a target handle are rejected: the edge
    // needs a rule id and the user must land on one of the per-rule source
    // handles (React Flow's native onConnect handles that precise path).
    const resolveProcessFlowDropId = useCallback((nodeId, fromHandleType)=>{
        if (isEndNodeId(nodeId)) return null;
        if (fromHandleType === "source" && isGatewayNodeId(nodeId)) {
            return resolveStepIdFromNodeId(nodeId);
        }
        if (fromHandleType === "target") {
            const node = getNode(nodeId);
            const data = node?.data;
            if (node?.type === "flowGateway" && data?.stepRecording?.actionType === "decision") {
                return null;
            }
        }
        return nodeId;
    }, [
        getNode
    ]);
    const handleConnect = useCallback(async (connection)=>{
        if (!connection.source || !connection.target) return;
        if (connection.source === connection.target) return;
        const sourceRuleId = extractRuleId(connection.sourceHandle);
        await onConnectSteps({
            sourceStepId: connection.source,
            targetStepId: connection.target,
            ...sourceRuleId ? {
                sourceRuleId
            } : {}
        });
    }, [
        onConnectSteps
    ]);
    const handleConnectStart = useCallback((_event, params)=>{
        if (params.handleType && params.nodeId) {
            startConnectionTracking(params.handleType, params.nodeId, {
                resolveDropId: resolveProcessFlowDropId
            });
        }
    }, [
        resolveProcessFlowDropId
    ]);
    const handleConnectEnd = useCallback((event, connectionState)=>{
        try {
            if (connectionState.isValid) return;
            if (!connectionState.fromNode || !connectionState.fromHandle) return;
            const fromType = connectionState.fromHandle.type;
            const fromNodeId = connectionState.fromNode.id;
            const fromHandleId = connectionState.fromHandle.id ?? null;
            const pointer = getPointerCoords(event);
            if (!pointer) return;
            const droppedId = resolveDropTarget(pointer.clientX, pointer.clientY, fromNodeId, fromType, resolveProcessFlowDropId);
            if (!droppedId) return;
            // For target-direction drops on a `+` gateway, the hover preview snapped
            // to the gateway itself — the step/rule rewrite happens here so the
            // edge's source is the owning step and carries the rule id (via a
            // synthetic `rule-<id>` source handle), preventing orphan decision
            // edges and bogus `sourceStepId: 'gateway-*'` payloads.
            let effectiveDroppedId = droppedId;
            let syntheticSourceHandle = null;
            if (fromType === "target" && isGatewayNodeId(droppedId)) {
                const parsed = parseGatewayNodeId(droppedId);
                if (parsed) {
                    effectiveDroppedId = parsed.stepId;
                    if (parsed.ruleId) syntheticSourceHandle = buildRuleHandleId(parsed.ruleId);
                }
            }
            if (effectiveDroppedId === fromNodeId) return;
            const [source, target] = fromType === "source" ? [
                fromNodeId,
                effectiveDroppedId
            ] : [
                effectiveDroppedId,
                fromNodeId
            ];
            const [sourceHandle, targetHandle] = fromType === "source" ? [
                fromHandleId,
                null
            ] : [
                syntheticSourceHandle,
                fromHandleId
            ];
            void handleConnect({
                source,
                target,
                sourceHandle,
                targetHandle
            });
        } finally{
            stopConnectionTracking();
        }
    }, [
        handleConnect,
        resolveProcessFlowDropId
    ]);
    return {
        handleConnect,
        handleConnectStart,
        handleConnectEnd
    };
}

//# sourceMappingURL=useEditorConnections.js.map